import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CreditCard, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useCheckoutStatus } from "@/hooks/useCheckoutStatus";

/**
 * Uygulama genelinde Stripe ödeme durumunu gösteren katman.
 *   started   → "Ödeme sayfası açıldı" bildirimi (canlı takip sürüyor)
 *   completed → başarı bildirimi + içeriğe dönüş
 *   failed    → hata ekranı (tekrar dene / durumu yeniden kontrol et)
 */
const CheckoutStatusWatcher = () => {
  const { attempt, dismiss, recheck, waiting } = useCheckoutStatus();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notified = useRef<string | null>(null);
  const [rechecking, setRechecking] = useState(false);

  const phase = attempt?.phase ?? null;
  const stamp = attempt ? `${attempt.startedAt}-${attempt.phase}` : null;

  useEffect(() => {
    if (!attempt || !stamp || notified.current === stamp) return;
    notified.current = stamp;

    if (attempt.phase === "started") {
      toast.info(t("checkoutStatus.startedTitle", "Ödeme sayfası açıldı"), {
        description: t(
          "checkoutStatus.startedDesc",
          "Ödemeni tamamladığında erişimin otomatik olarak açılacak.",
        ),
        icon: <CreditCard size={16} />,
      });
    }

    if (attempt.phase === "completed") {
      toast.success(t("checkoutStatus.completedTitle", "Ödemen onaylandı"), {
        description: t("checkoutStatus.completedDesc", "Erişimin şu anda açık, hemen devam edebilirsin."),
        icon: <CheckCircle2 size={16} />,
        action: attempt.redirect
          ? {
              label: t("checkoutStatus.goContent", "İçeriğe git"),
              onClick: () => {
                const target = attempt.redirect!;
                dismiss();
                navigate(target);
              },
            }
          : undefined,
      });
      const id = setTimeout(dismiss, 6000);
      return () => clearTimeout(id);
    }
  }, [attempt, stamp, t, navigate, dismiss]);

  const handleRecheck = async () => {
    setRechecking(true);
    const ok = await recheck();
    setRechecking(false);
    if (!ok) {
      toast.error(t("checkoutStatus.stillPending", "Ödeme henüz görünmüyor, birazdan tekrar dene."));
    }
  };

  const failed = phase === "failed" || phase === "canceled";

  return (
    <>
      {waiting && (
        <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 z-[60] w-[min(92vw,26rem)] rounded-xl border border-border/60 bg-card/95 backdrop-blur px-4 py-3 shadow-lg">
          <div className="flex items-start gap-3">
            <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-primary" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">
                {phase === "syncing"
                  ? t("checkoutStatus.syncingTitle", "Üyeliğin işleniyor")
                  : t("checkoutStatus.startedTitle", "Ödeme sayfası açıldı")}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {phase === "syncing"
                  ? t(
                      "checkoutStatus.syncingDesc",
                      "Ödemen alındıysa paket bilgin birkaç saniye içinde güncellenir. Bu ekranı kapatmana gerek yok.",
                    )
                  : t(
                      "checkoutStatus.startedDesc",
                      "Ödemeni tamamladığında erişimin otomatik olarak açılacak.",
                    )}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" onClick={handleRecheck} disabled={rechecking}>
                  {rechecking ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  {t("checkoutStatus.recheck", "Durumu yeniden kontrol et")}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={dismiss}>
                  {t("checkoutStatus.close", "Kapat")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

    <Dialog open={failed} onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-400" />
            {phase === "canceled"
              ? t("checkoutStatus.canceledTitle", "Ödeme yarıda kaldı")
              : t("checkoutStatus.failedTitle", "Ödemeni tamamlayamadık")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <p className="text-sm text-muted-foreground">
            {attempt?.reason === "timeout"
              ? t(
                  "checkoutStatus.timeoutDesc",
                  "Ödeme penceresi uzun süredir sonuçlanmadı. Kartından çekim yapıldıysa durumu yeniden kontrol edebilirsin.",
                )
              : t(
                  "checkoutStatus.failedDesc",
                  "Ödeme başlatılamadı. Tekrar deneyebilir veya havale/EFT ile devam edebilirsin.",
                )}
          </p>
          {attempt?.planLabel && (
            <p className="text-xs text-muted-foreground">
              {t("checkoutStatus.plan", "Seçilen paket")}: <span className="text-foreground">{attempt.planLabel}</span>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="flex-1 gap-2" onClick={handleRecheck} disabled={rechecking}>
              {rechecking ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
              {t("checkoutStatus.recheck", "Durumu yeniden kontrol et")}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                dismiss();
                navigate("/premium");
              }}
            >
              {t("checkoutStatus.retry", "Tekrar dene")}
            </Button>
          </div>
          <Button variant="ghost" size="sm" className="w-full" onClick={dismiss}>
            {t("checkoutStatus.close", "Kapat")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default CheckoutStatusWatcher;
